import React, { useEffect, useState } from 'react';
import { Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { DateTime } from 'luxon';
import { useForm, Controller } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { FacilityType, Paths } from '@/common/const';
import { useHttpContext } from '@/common/hooks/HttpContext';
import { toasts } from '@/common/toasts';
import { DashboardMeta, PatchLocationForReporting } from '@/common/types';
import { SubmitCancelButtons } from '@/components/common/Form/Form';
import { LoadingSpinner } from '@/components/common/LoadingSpinner/LoadingSpinner';
import { resultIsError } from '@/services/HttpService';

interface Props {
    locationId: number;
    organizationId: number | null;
    onLocationNameLoaded?: (name: string) => void;
}

interface FormValues {
    onboardingDate: DateTime | null;
    facilityType: FacilityType | null;
    dashboardMetaLevel1Id: number | null;
    dashboardMetaLevel2Id: number | null;
    dashboardMetaThirdPartyId: number | null;
    hidden: boolean | null;
}

export const AdminReportingLocationEditContainer = ({ locationId, organizationId, onLocationNameLoaded }: Props) => {
    const { httpService } = useHttpContext();
    const navigate = useNavigate();
    const { errorToast, successToast } = toasts;
    const [isLoading, setIsLoading] = useState(true);
    const [level1Options, setLevel1Options] = useState<DashboardMeta[]>([]);
    const [level2Options, setLevel2Options] = useState<DashboardMeta[]>([]);
    const [thirdPartyOptions, setThirdPartyOptions] = useState<DashboardMeta[]>([]);
    const [level1Label, setLevel1Label] = useState('Dashboard Level 1');
    const [level2Label, setLevel2Label] = useState('Dashboard Level 2');

    const { control, handleSubmit, reset } = useForm<FormValues>({
        defaultValues: {
            onboardingDate: null,
            facilityType: null,
            dashboardMetaLevel1Id: null,
            dashboardMetaLevel2Id: null,
            dashboardMetaThirdPartyId: null,
            hidden: false,
        },
    });

    useEffect(() => {
        (async () => {
            const [locationRes, orgRes] = await Promise.all([
                httpService.getLocationForReporting(locationId),
                organizationId ? httpService.getOrganizationForReporting(organizationId) : Promise.resolve(null),
            ]);

            if (resultIsError(locationRes)) {
                errorToast('Could not fetch location.');
            } else {
                onLocationNameLoaded?.(locationRes.name);
                reset({
                    onboardingDate: locationRes.onboardingDate ? DateTime.fromISO(locationRes.onboardingDate) : null,
                    facilityType: locationRes.facilityType ?? null,
                    dashboardMetaLevel1Id: locationRes.dashboardMetaLevel1?.id ?? null,
                    dashboardMetaLevel2Id: locationRes.dashboardMetaLevel2?.id ?? null,
                    dashboardMetaThirdPartyId: locationRes.dashboardMetaThirdParty?.id ?? null,
                    hidden: locationRes.hidden,
                });
            }

            if (orgRes && !resultIsError(orgRes)) {
                setLevel1Options(orgRes.dashboardMetaLevel1Records ?? []);
                setLevel2Options(orgRes.dashboardMetaLevel2Records ?? []);
                setThirdPartyOptions(orgRes.dashboardMetaThirdPartyRecords ?? []);
                if (orgRes.levelOneLabel) setLevel1Label(orgRes.levelOneLabel);
                if (orgRes.levelTwoLabel) setLevel2Label(orgRes.levelTwoLabel);
            }

            setIsLoading(false);
        })();
    }, [locationId]);

    const onSubmit = async (data: FormValues) => {
        const dto: PatchLocationForReporting = {
            onboardingDate: data.onboardingDate ? data.onboardingDate.toISODate() : null,
            facilityType: data.facilityType,
            dashboardMetaLevel1Id: data.dashboardMetaLevel1Id,
            dashboardMetaLevel2Id: data.dashboardMetaLevel2Id,
            dashboardMetaThirdPartyId: data.dashboardMetaThirdPartyId,
            hidden: data.hidden ?? false,
        };

        const res = await httpService.updateLocationForReporting(locationId, dto);
        if (resultIsError(res)) {
            errorToast('Could not update location.');
        } else {
            successToast('Location updated successfully.');
            navigate(`${Paths.adminReportingOrganizations}${organizationId}`);
        }
    };

    const handleCancel = () => {
        navigate(Paths.adminReporting, { state: { organizationId } });
    };

    if (isLoading) return <LoadingSpinner />;

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 400 }}
        >
            <Controller
                control={control}
                name="onboardingDate"
                render={({ field: { onChange, value } }) => (
                    <DatePicker label="Onboarding Date" value={value} onChange={onChange} />
                )}
            />
            <FormControl>
                <InputLabel id="facility-type-label">Facility Type</InputLabel>
                <Controller
                    control={control}
                    name="facilityType"
                    render={({ field: { onChange, value } }) => (
                        <Select
                            labelId="facility-type-label"
                            id="facility-type-select"
                            value={value ?? ''}
                            label="Facility Type"
                            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                        >
                            <MenuItem value="">None</MenuItem>
                            {Object.values(FacilityType).map((type) => (
                                <MenuItem value={type} key={type}>
                                    {type}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </FormControl>
            <FormControl>
                <InputLabel id="dashboard-level1-label">{level1Label}</InputLabel>
                <Controller
                    control={control}
                    name="dashboardMetaLevel1Id"
                    render={({ field: { onChange, value } }) => (
                        <Select
                            labelId="dashboard-level1-label"
                            id="dashboard-level1-select"
                            value={value ?? ''}
                            label={level1Label}
                            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                        >
                            <MenuItem value="">None</MenuItem>
                            {level1Options.map((option) => (
                                <MenuItem value={option.id} key={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </FormControl>
            <FormControl>
                <InputLabel id="dashboard-level2-label">{level2Label}</InputLabel>
                <Controller
                    control={control}
                    name="dashboardMetaLevel2Id"
                    render={({ field: { onChange, value } }) => (
                        <Select
                            labelId="dashboard-level2-label"
                            id="dashboard-level2-select"
                            value={value ?? ''}
                            label={level2Label}
                            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                        >
                            <MenuItem value="">None</MenuItem>
                            {level2Options.map((option) => (
                                <MenuItem value={option.id} key={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </FormControl>
            <FormControl>
                <InputLabel id="dashboard-third-party-label">Third Party</InputLabel>
                <Controller
                    control={control}
                    name="dashboardMetaThirdPartyId"
                    render={({ field: { onChange, value } }) => (
                        <Select
                            labelId="dashboard-third-party-label"
                            id="dashboard-third-party-select"
                            value={value ?? ''}
                            label="Third Party"
                            onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
                        >
                            <MenuItem value="">None</MenuItem>
                            {thirdPartyOptions.map((option) => (
                                <MenuItem value={option.id} key={option.id}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </Select>
                    )}
                />
            </FormControl>
            <Controller
                control={control}
                name="hidden"
                render={({ field: { onChange, value } }) => (
                    <FormControlLabel
                        control={<Checkbox checked={value ?? false} onChange={(e) => onChange(e.target.checked)} />}
                        label="Hidden"
                    />
                )}
            />
            <SubmitCancelButtons submitFunction={handleSubmit(onSubmit)} cancelFunction={handleCancel} />
        </form>
    );
};
